const TABBABLE_SELECTOR = [
	"a[href]",
	"area[href]",
	"input:not([disabled])",
	"select:not([disabled])",
	"textarea:not([disabled])",
	"button:not([disabled])",
	"[tabindex]:not([tabindex='-1'])",
	"[contenteditable='true']",
].join(", ");

/**
 * Marks controls that sit inside a focus scope for layout only, so the trap
 * and the selector's click-outside test treat them as outside it.
 */
export const OUTSIDE_FOCUS_TRAP =
	".gvtui-question-context-focus-scope__outside";

const isVisible = (element: HTMLElement): boolean => {
	if (element.getAttribute("aria-hidden") === "true") {
		return false;
	}
	if (typeof element.checkVisibility === "function") {
		return element.checkVisibility();
	}
	let node: HTMLElement | null = element;
	while (node) {
		const style = window.getComputedStyle(node);
		if (style.display === "none" || style.visibility === "hidden") {
			return false;
		}
		node = node.parentElement;
	}
	return true;
};

/** Focusable elements inside `root`, in document order, omitting hidden popover chrome. */
export function tabbablesWithin(root: HTMLElement): HTMLElement[] {
	return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
		(element) => {
			if (
				element.closest(".gvtui-question-context-selector--closed") ||
				element.closest(OUTSIDE_FOCUS_TRAP)
			) {
				return false;
			}
			return isVisible(element);
		},
	);
}

/**
 * Keeps Tab / Shift+Tab within `root` while a popover is open (APG dialog
 * focus containment, scoped to the question field rather than the whole page).
 */
export function handleTabTrap(root: HTMLElement, event: KeyboardEvent): boolean {
	if (event.key !== "Tab") {
		return false;
	}

	const items = tabbablesWithin(root);
	if (items.length === 0) {
		return false;
	}

	const active = document.activeElement;
	const currentIndex =
		active instanceof HTMLElement ? items.indexOf(active) : -1;

	if (event.shiftKey) {
		if (currentIndex <= 0) {
			event.preventDefault();
			items[items.length - 1]?.focus();
			return true;
		}
		return false;
	}

	if (currentIndex === -1 || currentIndex >= items.length - 1) {
		event.preventDefault();
		items[0]?.focus();
		return true;
	}

	return false;
}
